import { ChangeEvent, useState } from "react"

import { CellProps, useFocusManagement } from "components/TreeState"
import { ProgramOption, fetchAvailablePrograms } from "api/programs"
import { css, cx } from "@emotion/css"
import { queryCache, useQuery } from "react-query"

import Autocomplete from "@material-ui/lab/Autocomplete"
import { CellError } from "./CellError"
import { ChangedPlanRecord } from "../types"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import { MediaType } from "routes/Base/Admin/GlobalMediaTypes/types"
import { Paginated } from "api/types"
import { SupplierProductOptionTuple } from "models/supplier"
import TextField from "@material-ui/core/TextField"
import { Tooltip } from "@material-ui/core"
import { fetchSupplierMediaProducts } from "api/suppliers"
import { get } from "lodash"
import { set } from "lodash/fp"
import { useAutoCompute } from "../components/AutoCompute/useAutoCompute"
import { useParams } from "react-router-dom"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { useUndoStack } from "../useUndoStack"

const optionValueBasedOnType = (opt: ProgramOption) =>
  typeof opt === "string" ? opt : opt.programName

export function ProgramCell(
  props: CellProps & {
    onChange?: (value?: string) => void
  },
) {
  const { rowId: id, dataKey, columnId, disabled, onChange, primaryKey } = props
  const { id: planId } = useParams<{ id: string }>()
  const [isFocused, setIsFocused] = useState(false)

  const autoCompute = useAutoCompute()
  const tableSource = useTableSource()

  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const writeTo = tableSource.state

  const values = readFrom[id] || {}
  const mediaTypeId = values?.ancestorMediaId
  const error = get(readError, `${id}.${columnId}`)
  const action = values?.action
  const updatable = !action || action === "UPDATE_ORDER"
  const value = get(values, dataKey) as string

  const [textValue, setTextValue] = useState<string>(() => value || "")

  const ps = new URLSearchParams([
    ["configScope", "MEDIA_PLAN"],
    ["configOwnerId", planId],
  ])

  const mediaTypes = queryCache.getQueryData<Paginated<MediaType>>([
    "media-types",
    ps.toString(),
  ])

  const mediaFields = mediaTypes?.data.find(
    (mt) => mt.id === mediaTypeId,
  )?.mediaFields

  const spotsFieldId = mediaFields?.find(
    (mf) => mf.semanticCode === "spots",
  )?.id

  const programStartTimeId = mediaFields?.find(
    (mf) => mf.semanticCode === "programStartTime",
  )?.id

  const programEndTimeId = mediaFields?.find(
    (mf) => mf.semanticCode === "programEndTime",
  )?.id

  const spotDates =
    spotsFieldId &&
    get(values, `${primaryKey}.${spotsFieldId}.spots`)?.map(
      (spot: { date: string }) => spot.date,
    )

  const vendorProductFieldId = mediaFields?.find(
    (mf) => mf.semanticCode === "vendorProduct",
  )?.id

  const vendorProduct =
    vendorProductFieldId && get(values, `${primaryKey}.${vendorProductFieldId}`)

  //vendor products
  const mediaTypeSemanticCode = values.ancestorMediaSemanticCode

  const vpp = new URLSearchParams()
  !!mediaTypeSemanticCode &&
    vpp.set("mediaTypeSemanticCode", mediaTypeSemanticCode)
  vendorProduct?.mediaOwnerId &&
    vpp.set("mediaOwnerId", vendorProduct.mediaOwnerId)
  vendorProduct?.intermediaryId &&
    vpp.set("intermediaryId", vendorProduct.intermediaryId)

  const cached = queryCache.getQueryData<SupplierProductOptionTuple[]>([
    "vendor-products",
    planId,
    vpp.toString(),
  ])

  const { data: vendorProducts } = useQuery(
    ["vendor-products", planId, vpp.toString()],
    fetchSupplierMediaProducts,
    {
      enabled: !!mediaTypeSemanticCode && !cached,
      initialData: cached,
      refetchOnMount: !cached,
    },
  )

  const channelName = vendorProducts?.find(
    (vd) => vd?.id === vendorProduct?.supplierProductId,
  )?.name

  const readonly =
    readFrom[id]?.type === "media_subtype_plan" ||
    readFrom[id]?.type === "media_type_plan" ||
    readFrom[id]?.type === "package"

  const availProgramsParams = new URLSearchParams()
  channelName && availProgramsParams.set("channel", channelName)
  spotDates?.length &&
    availProgramsParams.set("broadcastDates", spotDates.join(","))

  const { data: options, isLoading } = useQuery(
    ["available-programs", availProgramsParams.toString()],
    fetchAvailablePrograms,
    {
      enabled: isFocused && !(disabled || readonly),
    },
  )

  const applyFinalChange = (value?: string, isPackage?: boolean) => {
    tableSource.clearError(id)
    globalUndoState.push([writeTo[id] as ChangedPlanRecord])
    writeTo[id] = set(dataKey, value, writeTo[id])
    updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
    onChange && onChange(value)
  }

  const handleChange = (
    _event: ChangeEvent<{}>,
    selected: ProgramOption | null,
  ) => {
    const finalChange = selected && optionValueBasedOnType(selected)
    const startTime =
      typeof selected !== "string" ? selected?.programStartTime : undefined
    const endTime =
      typeof selected !== "string" ? selected?.programEndTime : undefined
    if (startTime && programStartTimeId) {
      writeTo[id] = set(
        `${primaryKey}.${programStartTimeId}`,
        startTime,
        writeTo[id],
      )
    }
    if (endTime && programEndTimeId) {
      writeTo[id] = set(
        `${primaryKey}.${programEndTimeId}`,
        endTime,
        writeTo[id],
      )
    }
    applyFinalChange(finalChange || undefined)
  }

  const handleInputChange = (_event: ChangeEvent<{}>, value: string) => {
    setTextValue(value)
  }

  const handleBlur = () => {
    setIsFocused(false)
    if (textValue !== value) {
      const finalValue = textValue !== "" ? textValue : undefined
      applyFinalChange(finalValue)
    }
    autoCompute.compute(writeTo[id] as ChangedPlanRecord)
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const { getInputProps } = useFocusManagement()
  const ip = getInputProps()

  const isDisabled = disabled || readonly

  const loading = isLoading

  return (
    <>
      <Tooltip title={textValue} placement="bottom-start">
        <Autocomplete
          freeSolo
          loading={loading}
          disabled={isDisabled}
          classes={{
            option: styles.option,
            endAdornment: isDisabled ? styles.hidden : undefined,
          }}
          getOptionLabel={optionValueBasedOnType}
          renderOption={(opt: ProgramOption) =>
            typeof opt === "string" ? (
              <>{opt}</>
            ) : (
              <>{`${opt.programName} ${opt.programStartTime}-${opt.programEndTime}`}</>
            )
          }
          onChange={handleChange}
          value={textValue || ""}
          onBlur={handleBlur}
          onInputChange={handleInputChange}
          options={options || []}
          openOnFocus={false}
          onFocus={handleFocus}
          disableClearable={true}
          onKeyDown={ip.onKeyDown}
          filterOptions={(options) => {
            return options.filter((opt) =>
              optionValueBasedOnType(opt)
                .toUpperCase()
                .includes(textValue.toUpperCase()),
            )
          }}
          popupIcon={<DropdownIcon className="icon" />}
          style={{
            width: "inherit",
            height: "inherit",
            display: "flex",
          }}
          className={styles.auto}
          renderInput={(params) => (
            <TextField
              {...params}
              autoComplete="off"
              onFocus={ip.onFocus}
              onBlur={ip.onBlur}
              fullWidth
              name={dataKey}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                classes: {
                  root: css`
                    font-size: 13px;
                    font-weight: inherit;
                    padding: 0 !important;
                    height: 100%;
                    &.Mui-disabled {
                      color: inherit;
                    }
                  `,
                  input: cx(styles.input),
                },
                ...params.InputProps,
              }}
            />
          )}
        />
      </Tooltip>
      {error ? <CellError error={error} /> : null}
    </>
  )
}

const styles = {
  auto: css`
    .icon {
      opacity: 0;
    }
    :hover {
      .icon {
        opacity: 1;
      }
    }
  `,
  option: css`
    font-size: 13px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  `,
  input: css`
    padding: 0 !important;
    font-size: 13px;
    font-weight: inherit;
  `,
  hidden: css`
    display: none;
  `,
}
